import { useEffect, useMemo, useRef } from 'react';
import uPlot from 'uplot';

import type { Field, Sample } from '@/api';
import { formatValue, unitLabel } from '@/lib/format';

const cssVar = (name: string) => getComputedStyle(document.documentElement).getPropertyValue(name).trim() || '#888';

/** Numeric series of one field from samples (booleans → 0/1). */
export function seriesOf(samples: Sample[], key: string): [number[], (number | null)[]] {
	const xs: number[] = [];
	const ys: (number | null)[] = [];
	for (const s of samples) {
		xs.push(Date.parse(s.ts) / 1000);
		const v = s.values[key];
		ys.push(typeof v === 'number' ? v : typeof v === 'boolean' ? Number(v) : null);
	}
	return [xs, ys];
}

function LineChart({ xs, ys, height = 110, step = false }: { xs: number[]; ys: (number | null)[]; height?: number; step?: boolean }) {
	const el = useRef<HTMLDivElement>(null);
	const plot = useRef<uPlot | null>(null);

	useEffect(() => {
		const host = el.current!;
		const stroke = cssVar('--color-series-1');
		const grid = cssVar('--color-border');
		const text = cssVar('--color-fg-muted');
		const axis = { stroke: text, grid: { stroke: grid, width: 1 }, ticks: { stroke: grid, width: 1 }, font: '10px ui-sans-serif, system-ui' };
		const opts: uPlot.Options = {
			width: host.clientWidth || 300,
			height,
			cursor: { drag: { x: false, y: false } },
			legend: { show: false },
			scales: { x: { time: true } },
			axes: [
				{ ...axis, size: 26 },
				// Width follows the longest tick label (e.g. "12,000.04" on an energy counter).
				{ ...axis, size: (_u, values) => 14 + 6 * Math.max(3, ...(values ?? []).map((v) => String(v).length)) },
			],
			series: [{}, { stroke, width: 1.5, fill: `color-mix(in oklch, ${stroke} 14%, transparent)`, paths: step ? uPlot.paths.stepped!({ align: 1 }) : undefined, points: { show: false } }],
		};
		plot.current = new uPlot(opts, [[], []], host);
		const ro = new ResizeObserver(() => plot.current?.setSize({ width: host.clientWidth, height }));
		ro.observe(host);
		return () => {
			ro.disconnect();
			plot.current?.destroy();
			plot.current = null;
		};
	}, [height, step]);

	useEffect(() => {
		plot.current?.setData([xs, ys] as uPlot.AlignedData);
	}, [xs, ys]);

	return <div ref={el} className="w-full" />;
}

/** One small chart per field, with the latest value as the headline. */
export function SmallMultiples({ fields, samples, height }: { fields: Field[]; samples: Sample[]; height?: number }) {
	const series = useMemo(() => fields.map((f) => seriesOf(samples, f.key)), [fields, samples]);
	const last = samples[samples.length - 1];
	return (
		<div className="grid grid-cols-[repeat(auto-fill,minmax(17rem,1fr))] gap-3">
			{fields.map((f, i) => (
				<div key={f.key} className="rounded-md border border-border bg-surface p-3">
					<div className="mb-1 flex items-baseline justify-between gap-2">
						<span className="truncate text-xs text-fg-muted">
							{f.label}
							{f.unit && <span className="opacity-70"> · {unitLabel(f.unit)}</span>}
						</span>
						<span className="font-mono text-sm font-semibold tabular-nums">{formatValue(f, last?.values[f.key])}</span>
					</div>
					<LineChart xs={series[i]![0]} ys={series[i]![1]} height={height} step={f.kind === 'boolean' || f.kind === 'discrete'} />
				</div>
			))}
		</div>
	);
}

/** Tiny inline trend for tables. */
export function Sparkline({ values, width = 80, height = 22 }: { values: number[]; width?: number; height?: number }) {
	if (values.length < 2) return <span className="inline-block" style={{ width, height }} />;
	const min = Math.min(...values);
	const max = Math.max(...values);
	const span = max - min || 1;
	const pts = values.map((v, i) => `${((i / (values.length - 1)) * width).toFixed(1)},${(height - 2 - ((v - min) / span) * (height - 4)).toFixed(1)}`).join(' ');
	return (
		<svg width={width} height={height} className="text-accent" aria-hidden>
			<polyline points={pts} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
		</svg>
	);
}
